/**
 * What an account without a subscription sees: one card that asks where the agent works and
 * answers with the price, the counties it covers and the rest of their area to add, then shows
 * what those counties brought in the last 30 days before asking for anything.
 *
 * The agent never chooses a plan; the price follows from the counties (`planFor`). Until
 * billing exists, starting is a request the admin answers by granting the counties, and the
 * page says that nothing is charged.
 */
import { LockIcon } from "lucide-react";
import * as React from "react";
import type { Billing, CountyProof, PlanChoice, PricedCounty, PricingCatalogue } from "@/api/models";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { api } from "@/api/client";
import { usePlanChoice, usePlans, useProof, useRequestPlan } from "@/api/queries";
import { HouseBadge } from "@/components/lead-bits";
import { ErrorState, Loading } from "@/components/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { prettyDay } from "@/lib/dates";
import {
  type Chosen,
  MAX_COUNTIES,
  areaCounties,
  areaName,
  choose,
  countiesWords,
  planFor,
  priceWords,
  startingCounties,
} from "@/lib/plans";
import { openDemo } from "@/lib/demo";
import { useAccount } from "@/lib/session";
import { holdTitle } from "@/lib/title";
import { FormError } from "@/pages/sign-in";

/** "3 hours ago", "4 days ago": how long since the court was last read. */
function since(moment: string, now = Date.now()): string {
  const hours = Math.max(0, Math.floor((now - Date.parse(moment)) / 3_600_000));
  if (hours < 1) return "less than an hour ago";
  if (hours < 48) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  return `${Math.floor(hours / 24)} days ago`;
}

function plural(count: number, one: string, many: string): string {
  return `${count.toLocaleString("en-US")} ${count === 1 ? one : many}`;
}

function Figure({ value, label, note }: { value: string; label: string; note?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 rounded-md border border-border bg-surface-raised px-3 py-2.5">
      <span className="font-mono text-xl leading-tight text-heading tabular-nums max-sm:text-lg">{value}</span>
      <span className="text-sm text-ink max-sm:text-xs">{label}</span>
      {note ? <span className="text-xs text-muted">{note}</span> : null}
    </div>
  );
}

/** One county's month: its figures, when its court was last read, and a few recent estates with the family hidden. */
function CountyMonth({ proof }: { proof: CountyProof }) {
  const name = proof.county.display;
  const checked = proof.last_court_check ? (
    proof.court_stale ? (
      <Badge variant="warning" size="sm" dot>
        Last court check {since(proof.last_court_check)}
      </Badge>
    ) : (
      <span className="text-xs text-muted">Court checked {since(proof.last_court_check)}</span>
    )
  ) : (
    <Badge variant="warning" size="sm" dot>
      Court not read yet
    </Badge>
  );
  let houses: React.ReactNode;
  if (proof.houses_in_deceased_name) {
    houses = (
      <Figure
        value={proof.houses_in_deceased_name.toLocaleString("en-US")}
        label="houses still in the deceased's name"
        note={`of ${plural(proof.houses_found, "house", "houses")} found`}
      />
    );
  } else if (proof.houses_found) {
    houses = <Figure value={proof.houses_found.toLocaleString("en-US")} label="houses found" note="ownership not confirmed yet" />;
  } else {
    houses = <Figure value="None yet" label="houses found" />;
  }
  return (
    <section className="flex flex-col gap-3" data-testid="county-proof" data-county={proof.county.slug}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className="m-0 text-base font-semibold text-heading">{name}</h3>
        {checked}
      </div>
      {proof.status === "ready" ? (
        <>
          <div className="grid grid-cols-3 gap-2">
            <Figure
              value={proof.filed.toLocaleString("en-US")}
              label={proof.filed_label === "Filed" ? "estates filed" : `estates, by ${proof.filed_label.toLowerCase()}`}
            />
            <Figure value={proof.approvals.toLocaleString("en-US")} label={proof.approvals === 1 ? "court approval" : "court approvals"} />
            {houses}
          </div>
          {proof.estates.length ? (
            <>
              <ul className="m-0 flex list-none flex-col p-0" aria-label={`Recent approvals in ${name}`}>
                {proof.estates.map((estate, index) => (
                  <li
                    key={index}
                    className="grid grid-cols-[minmax(6rem,8rem)_minmax(8rem,9.5rem)_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 border-t border-border py-2.5 text-sm first:border-t-0 max-sm:grid-cols-[1fr_auto]"
                  >
                    <span className="truncate font-medium text-heading">{estate.city}</span>
                    <span className="whitespace-nowrap text-muted max-sm:text-end">
                      {estate.days_since_approval === 0
                        ? "Approved today"
                        : `Approved ${plural(estate.days_since_approval, "day", "days")} ago`}
                    </span>
                    <span className="min-w-0">
                      <HouseBadge status={estate.house_status} />
                    </span>
                    <span className="whitespace-nowrap font-mono text-xs text-ink tabular-nums max-sm:text-end">
                      {estate.value_range ?? "Value not on the roll"}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="m-0 inline-flex items-center gap-1.5 text-xs text-muted">
                <LockIcon aria-hidden className="size-3.5" />
                The family's names and contact details open when you unlock an estate.
              </p>
            </>
          ) : null}
        </>
      ) : (
        <p className="m-0 text-sm text-ink">
          {proof.status === "updating"
            ? `${name}'s figures are being recomputed. They're back within a few hours.`
            : `Figures for ${name} aren't available yet.`}
        </p>
      )}
    </section>
  );
}

/** What the chosen counties brought in the last 30 days. */
function Proof({ counties }: { counties: PricedCounty[] }) {
  const proof = useProof(counties.map((county) => county.slug));
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border bg-surface-soft p-4" id="proof">
      <div className="flex flex-col gap-0.5">
        <h2 className="m-0 text-sm font-semibold text-heading">The last 30 days in the court record</h2>
        <p className="m-0 text-xs text-muted">
          {proof.data ? `As of ${prettyDay(proof.data.as_of)}, ` : ""}from the court record and the county's property roll.
        </p>
      </div>
      {proof.isPending ? <Loading label="Loading the figures" /> : null}
      {proof.isError ? <ErrorState error={proof.error} onRetry={() => void proof.refetch()} /> : null}
      {proof.data?.counties.map((county) => (
        <CountyMonth key={county.county.slug} proof={county} />
      ))}
    </div>
  );
}

/** The counties of the agent's area to add, each with the price it leads to. */
function AreaCounties({
  catalogue,
  chosen,
  onChange,
}: {
  catalogue: PricingCatalogue;
  chosen: Chosen;
  onChange: (counties: PricedCounty[]) => void;
}) {
  const [home] = chosen.counties;
  if (!home) return null;
  const others = areaCounties(catalogue, home);
  const area = areaName(catalogue, home);
  if (!others.length) {
    return <p className="m-0 text-sm text-muted">No other county in the {area} area is open yet.</p>;
  }
  const full = chosen.counties.length >= MAX_COUNTIES;
  return (
    <fieldset className="m-0 flex flex-col gap-2 border-0 p-0" id="area-counties">
      <legend className="mb-2 p-0 text-sm font-medium text-heading">Add counties in the {area} area</legend>
      {others.map((county) => {
        const on = chosen.counties.includes(county);
        if (!on && full) return null;
        const grown = planFor(catalogue, [...chosen.counties, county]);
        const hint = on
          ? "Included"
          : grown && grown.key !== chosen.plan.key
            ? `${priceWords(grown, chosen.billing)} with it`
            : "Same price";
        const id = `area-${county.slug}`;
        return (
          <label
            key={county.slug}
            htmlFor={id}
            className="flex min-h-(--touch-min) cursor-pointer items-center gap-3 rounded-md border border-border bg-surface-raised px-3 py-2 text-sm hover:border-border-strong"
          >
            <Checkbox
              id={id}
              checked={on}
              onCheckedChange={(value) =>
                onChange(value ? [...chosen.counties, county] : chosen.counties.filter((item) => item !== county))
              }
            />
            <span className="flex-1 text-heading">
              {county.display}
              {county.opening_soon ? <span className="text-muted">, opening soon</span> : null}
            </span>
            <span className="text-xs text-muted">{hint}</span>
          </label>
        );
      })}
      <p className="m-0 text-xs text-muted">
        {full ? `Three counties is the most one plan holds.` : `Up to three counties of one area on one plan.`}
      </p>
    </fieldset>
  );
}

/** Where the agent works, the price it comes to, and what it brings; started in one click. */
function ChooseCounties({
  catalogue,
  initial,
  onDone,
  onCancel,
}: {
  catalogue: PricingCatalogue;
  initial: Chosen | null;
  onDone: () => void;
  onCancel?: () => void;
}) {
  const [counties, setCounties] = React.useState<PricedCounty[]>(initial?.counties ?? []);
  const [billing, setBilling] = React.useState<Billing>(initial?.billing ?? "month");
  const chosen = choose(
    catalogue,
    counties.map((county) => county.slug),
    billing,
  );
  const ask = useRequestPlan();
  const pay = useMutation({
    mutationFn: api.startCheckout,
    // Stripe's page takes over; the button stays busy until the browser has left.
    onSuccess: ({ url }) => window.location.assign(url),
  });
  const paying = catalogue.payments !== "off";
  const states = [...new Set(catalogue.counties.map((item) => item.state))];
  const [home] = counties;
  return (
    <form
      className="flex flex-col gap-6"
      id="choose-plan"
      onSubmit={(event) => {
        event.preventDefault();
        if (!chosen) return;
        const body = { counties: chosen.counties.map((county) => county.slug), billing: chosen.billing };
        if (paying) pay.mutate(body);
        else ask.mutate(body, { onSuccess: onDone });
      }}
    >
      <div className="flex flex-col gap-2">
        <Label htmlFor="plan-county">Where do you work?</Label>
        <Select
          value={home?.slug ?? ""}
          onValueChange={(value) => {
            const found = catalogue.counties.find((item) => item.slug === value);
            if (found) setCounties(startingCounties(catalogue, found));
          }}
        >
          <SelectTrigger id="plan-county">
            <SelectValue placeholder="Choose your county" />
          </SelectTrigger>
          <SelectContent>
            {states.map((state, index) => (
              <React.Fragment key={state}>
                {index ? <SelectSeparator /> : null}
                {catalogue.counties
                  .filter((item) => item.state === state)
                  .map((item) => (
                    <SelectItem key={item.slug} value={item.slug}>
                      {item.display}
                      {item.opening_soon ? ", opening soon" : ""}
                    </SelectItem>
                  ))}
              </React.Fragment>
            ))}
          </SelectContent>
        </Select>
      </div>
      {chosen ? (
        <>
          <div className="flex flex-wrap items-end justify-between gap-4" id="chosen-price">
            <div className="flex flex-col gap-1">
              <p className="m-0 flex items-baseline gap-2">
                <span className="font-display text-4xl leading-none text-heading tabular-nums">
                  ${chosen.billing === "year" ? chosen.plan.yearly.toLocaleString("en-US") : chosen.plan.monthly}
                </span>
                <span className="text-muted">{chosen.billing === "year" ? "a year" : "a month"}</span>
              </p>
              <p className="m-0 text-sm text-ink" id="chosen-counties">
                Leads from {countiesWords(chosen.counties)}.
              </p>
            </div>
            <fieldset className="m-0 flex gap-4 border-0 p-0 text-sm">
              <legend className="sr-only">Billing</legend>
              {(["month", "year"] as const).map((value) => (
                <label key={value} className="inline-flex min-h-(--touch-min) cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name="billing"
                    value={value}
                    checked={billing === value}
                    onChange={() => setBilling(value)}
                    className="size-4 accent-action"
                  />
                  {value === "month" ? "Monthly" : "Yearly, two months free"}
                </label>
              ))}
            </fieldset>
          </div>
          {chosen.counties.some((county) => county.opening_soon) ? (
            <p className="m-0 text-sm text-ink">
              {countiesWords(chosen.counties.filter((county) => county.opening_soon))} is opening soon. Ask now, and it starts the day it
              opens.
            </p>
          ) : null}
          <AreaCounties catalogue={catalogue} chosen={chosen} onChange={setCounties} />
          <p className="m-0 text-sm text-ink" id="unlock-allowance">
            Unlock up to 20 estates a month. Unlocking an estate shows the family's details and adds it to your follow-up.
          </p>
          <Proof counties={chosen.counties} />
        </>
      ) : null}
      <FormError error={paying ? pay.error : ask.error} />
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="lg" id="start-plan" disabled={!chosen || ask.isPending || pay.isPending || pay.isSuccess}>
          {paying
            ? pay.isPending || pay.isSuccess
              ? "Opening the payment page"
              : "Continue to payment"
            : ask.isPending
              ? "Sending"
              : "Start my subscription"}
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Keep my request
          </Button>
        ) : null}
      </div>
      {catalogue.payments === "test" ? (
        <p className="m-0 text-sm text-muted" id="test-mode">
          Test mode: no card is charged. Pay with the test card 4242 4242 4242 4242, any future date and any security code.
        </p>
      ) : paying ? (
        <p className="m-0 text-sm text-muted">
          Stripe takes the payment. Cancel any time from your account; access lasts to the end of the period paid.
        </p>
      ) : null}
    </form>
  );
}

/** How many times the return reads the payment back, two seconds apart, before saying it is not confirmed yet. */
const READS = 15;

/** Back from Stripe's page: the payment is read back from Stripe until it is paid, and the
 * counties then open; an abandoned or expired payment says so and charges nothing. */
function CheckoutReturn({ id, onDone }: { id: string; onDone: () => void }) {
  const client = useQueryClient();
  const outcome = useQuery({
    queryKey: ["checkout", id],
    queryFn: () => api.checkout(id),
    refetchInterval: (query) => (query.state.data?.status === "pending" && query.state.dataUpdateCount < READS ? 2000 : false),
  });
  const status = outcome.data?.status;
  const tries = client.getQueryState(["checkout", id])?.dataUpdateCount ?? 0;
  React.useEffect(() => {
    // The counties load first, then the address loses the checkout it no longer needs.
    if (status === "paid") void client.invalidateQueries().then(onDone);
  }, [status, client, onDone]);
  if (outcome.isError) return <ErrorState error={outcome.error} onRetry={() => void outcome.refetch()} />;
  if (status === "expired" || (status === "pending" && tries >= READS)) {
    return (
      <div className="flex flex-col gap-3" id="checkout-return">
        <p className="m-0 text-sm text-ink">
          {status === "expired"
            ? "That payment page expired before it was paid. Nothing was charged."
            : "Stripe has not confirmed the payment yet. If you paid, your counties open within the hour; nothing is charged twice."}
        </p>
        <Button variant="outline" className="self-start" onClick={onDone}>
          Back to your counties
        </Button>
      </div>
    );
  }
  return <Loading label={status === "paid" ? "Opening your counties" : "Confirming your payment with Stripe"} />;
}

function asChosen(choice: PlanChoice | null | undefined): Chosen | null {
  return choice ? { counties: choice.counties, plan: choice.plan, billing: choice.billing } : null;
}

export function StartPlan() {
  React.useEffect(() => {
    holdTitle("Start your subscription | EstateLark");
    return () => holdTitle(null);
  }, []);
  const plans = usePlans();
  const choice = usePlanChoice();
  const account = useAccount();
  const [changing, setChanging] = React.useState(false);
  const [params, setParams] = useSearchParams();
  const returned = params.get("checkout");
  const clearReturn = React.useCallback(() => {
    setParams(
      (now) => {
        now.delete("checkout");
        return now;
      },
      { replace: true },
    );
  }, [setParams]);
  const chosen = asChosen(choice.data);
  // With payments on, a request is a checkout started, never a wait for the admin.
  const asked = plans.data?.payments === "off" && Boolean(choice.data?.requested_at) && !changing;

  let title = "Choose your county";
  let line = "See what your county brought in the last 30 days, then start when you're ready.";
  if (asked && chosen) {
    title = "Your request is in";
    line = `We'll start your subscription and email ${account.email} when your leads are ready. Nothing is charged yet.`;
  } else if (returned && returned !== "cancelled") {
    title = "Confirming your payment";
    line = "This takes a few seconds.";
  } else if (chosen && !changing) {
    title = `Start ${countiesWords(chosen.counties)}`;
  }

  return (
    <div id="start" className="mx-auto flex w-full max-w-[44rem] flex-col gap-6 pt-6 max-sm:pt-0">
      <header className="flex flex-col gap-2">
        <h1 id="start-title" className="m-0 font-display text-3xl leading-tight text-heading">
          {title}
        </h1>
        <p className="m-0 text-base text-ink">{line}</p>
      </header>
      <Card className="p-6 max-sm:p-4">
        {plans.isPending || choice.isPending ? <Loading label="Loading your plan" /> : null}
        {plans.isError ? <ErrorState error={plans.error} onRetry={() => void plans.refetch()} /> : null}
        {choice.isError ? <ErrorState error={choice.error} onRetry={() => void choice.refetch()} /> : null}
        {returned === "cancelled" ? (
          <p className="m-0 mb-5 rounded-md bg-surface-soft px-4 py-3 text-sm text-ink" id="checkout-cancelled">
            Payment not finished. Nothing was charged; start again when you're ready.
          </p>
        ) : null}
        {returned && returned !== "cancelled" ? (
          <CheckoutReturn id={returned} onDone={clearReturn} />
        ) : plans.data && choice.isSuccess ? (
          asked && chosen ? (
            <div className="flex flex-col gap-4">
              <p className="m-0 flex flex-col gap-0.5 text-sm" id="requested-plan">
                <span className="font-semibold text-heading">{countiesWords(chosen.counties)}</span>
                <span className="text-muted">
                  {chosen.plan.name}, {priceWords(chosen.plan, chosen.billing)}
                </span>
              </p>
              <Button variant="link" className="self-start px-0" onClick={() => setChanging(true)} id="change-plan">
                Change my counties
              </Button>
            </div>
          ) : (
            <ChooseCounties
              key={chosen ? "chosen" : "new"}
              catalogue={plans.data}
              initial={chosen}
              onDone={() => setChanging(false)}
              onCancel={choice.data?.requested_at ? () => setChanging(false) : undefined}
            />
          )
        ) : null}
      </Card>
      <section className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-border px-5 py-4" id="sample-offer">
        <div className="flex max-w-[28rem] flex-col gap-0.5">
          <h2 className="m-0 text-base font-semibold text-heading">See the work before you start</h2>
          <p className="m-0 text-sm text-ink">
            A sample workspace with invented families: Today's list, an estate, logging a call and the follow-up it schedules.
          </p>
        </div>
        <Button variant="outline" onClick={openDemo} id="open-sample">
          Open the sample workspace
        </Button>
      </section>
    </div>
  );
}
